/**
 * Schémas Zod CRM — clients.
 */
import { z } from 'zod';
import { CLIENT_STATUSES, CLIENT_TYPES, CRM_COMPANY_SCOPES } from '../enums/crm.enum.js';
import { normalizeFirstName, normalizeLastName, toFirstNameCase, toLastNameCase, } from '../utils/format.utils.js';
import { emailSchema } from './auth.schema.js';
import { paginationQuerySchema, searchSchema, uuidSchema } from './common.schema.js';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** Champs d'identité du tiers : la casse saisie est normalisée (nom en capitales, prénom capitalisé). */
const optionalFirstName = optionalText(80).transform(normalizeFirstName);
const optionalLastName = optionalText(80).transform(normalizeLastName);
const typedClientKinds = CLIENT_TYPES.filter((item) => item !== 'public');
const crmContactInputSchema = z.object({
    firstName: z.string().trim().min(1).max(80).transform(toFirstNameCase),
    lastName: z.string().trim().min(1).max(80).transform(toLastNameCase),
    jobTitle: optionalText(80),
    email: emailSchema.nullable().optional(),
    mobilePhone: optionalText(30),
    landlinePhone: optionalText(30),
    secondaryPhone: optionalText(30),
    isPrimary: z.boolean().optional(),
});
const clientBaseSchema = {
    type: z.enum(typedClientKinds),
    companyScope: z.enum(CRM_COMPANY_SCOPES).nullable().optional(),
    companyName: optionalText(180),
    civility: optionalText(60),
    firstName: optionalFirstName,
    lastName: optionalLastName,
    email: emailSchema.nullable().optional(),
    mobilePhone: optionalText(30),
    landlinePhone: optionalText(30),
    secondaryPhone: optionalText(30),
    jobTitle: optionalText(80),
    addressLine1: optionalText(180),
    addressLine2: optionalText(180),
    postalCode: optionalText(16),
    city: optionalText(120),
    countryCode: optionalText(2),
    siren: optionalText(9),
    siret: optionalText(14),
    rnaNumber: optionalText(20),
    status: z.enum(CLIENT_STATUSES).optional(),
    source: optionalText(60),
    sellerUserId: uuidSchema.nullable().optional(),
    assigneeUserId: uuidSchema.nullable().optional(),
    originProspectId: uuidSchema.nullable().optional(),
    contacts: z.array(crmContactInputSchema).max(20).optional(),
};
function refineClientIdentity(value, ctx) {
    if (value.type === 'individual') {
        if (value.firstName === null ||
            value.firstName === undefined ||
            value.firstName.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['firstName'],
                message: 'Le prénom est obligatoire pour un client particulier.',
            });
        }
        if (value.lastName === null || value.lastName === undefined || value.lastName.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastName'],
                message: 'Le nom est obligatoire pour un client particulier.',
            });
        }
        return;
    }
    if (value.companyName === null ||
        value.companyName === undefined ||
        value.companyName.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyName'],
            message: 'La raison sociale est obligatoire pour ce type de client.',
        });
    }
    if (value.type === 'company' &&
        (value.companyScope === null || value.companyScope === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyScope'],
            message: 'Le périmètre géographique de la société est obligatoire.',
        });
    }
}
/** Paramètres de `GET /clients`. */
export const clientListQuerySchema = paginationQuerySchema.extend({
    search: searchSchema,
    filter: z
        .object({
        status: z.enum(CLIENT_STATUSES).optional(),
        type: z.enum(CLIENT_TYPES).optional(),
        sellerUserId: uuidSchema.optional(),
        assigneeUserId: uuidSchema.optional(),
    })
        .optional(),
});
/** Corps de `POST /clients`. */
export const clientCreateSchema = z.object(clientBaseSchema).superRefine(refineClientIdentity);
/** Corps de `PATCH /clients/:id`. */
export const clientUpdateSchema = z
    .object(clientBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
//# sourceMappingURL=client.schema.js.map